import React, { useEffect, lazy } from 'react'
import { Dispatch } from 'redux'
import { InfoOutlined } from '@material-ui/icons'
import { ICapability } from '../../types/capability'
import { IPublishingLocation } from '../../types/publishingLocations'
import { Modals } from '../../types/modal'
import { getCapabilityById, deleteCapability } from '../../actions/capability'
import { modalClose, modalOpen } from '../../actions/modal'
import { load } from '../../utils/load'
import { Main, Scrollable, SettingFormsWrapper } from '../../layouts'
import {
  CombinedTitle,
  TabsHead,
  View,
  SettingsMenu,
  P,
  SettingDeleteArea,
} from '../../components'
import { UpdateCapabilityForm } from '../../forms'

// Lazy load modules
const Modal = load(lazy(() => import('../../components/Modal')))

export interface ICapabilitySettingsPage {
  match: { params: { id: string } }
  capability: ICapability
  publishingLocations: IPublishingLocation[]
  activeModal?: string
  editable: boolean
  dispatch: Dispatch
}

export const CapabilitySettingsPage = ({
  match: {
    params: { id },
  },
  publishingLocations,
  capability,
  activeModal,
  editable,
  dispatch,
}: ICapabilitySettingsPage) => {
  useEffect(() => {
    dispatch(getCapabilityById(id))
  }, [id, dispatch])
  return (
    <Main
      sidebarSecondary={<SettingsMenu />}
      titleBlock={
        <>
          <CombinedTitle
            first={capability.title}
            second="Capability Settings"
          />
          <TabsHead
            tabs={[
              {
                label: 'Details',
                value: 'details',
                icon: <InfoOutlined />,
              },
            ]}
          />
        </>
      }
      helmet="Administration: Group Capabilities"
    >
      <View keep tab="details">
        <Scrollable>
          <SettingFormsWrapper>
            <UpdateCapabilityForm
              capability={capability}
              publishingLocations={publishingLocations}
              dispatch={dispatch}
            />
            <SettingDeleteArea
              onClickHandler={() =>
                dispatch(modalOpen(Modals.deleteConfirmModal))
              }
              texts={{
                title: 'Delete capability',
                description:
                  'Note that this action is not reversible and cannot be undone.',
                buttonLabel: 'Delete this Capability',
              }}
              buttonProps={{ id: 'delete-capability-dialog' }}
              disabled={!editable}
            />
            <Modal
              open={activeModal === Modals.deleteConfirmModal}
              handleClose={() => {
                dispatch(modalClose())
              }}
              action={() => {
                dispatch(deleteCapability(capability.id))
                dispatch(modalClose())
              }}
              title="Capability deletion"
              actionLabel="Delete"
            >
              <P mb="true">
                This Capability <strong>{capability.title}</strong> will be
                deleted - please note that this action is not reversible and
                cannot be undone.
              </P>
              <P>
                Are you sure you want to delete the Capability{' '}
                <strong>{capability.title}</strong>?
              </P>
            </Modal>
            <Modal
              id="delete-capability-modal-dialog"
              type="info"
              open={activeModal === Modals.deleteStatusModal}
              handleClose={() => {
                dispatch(modalClose())
              }}
              title={`The capability ${capability.title} can’t be deleted`}
            >
              <P>{capability.message}</P>
            </Modal>
          </SettingFormsWrapper>
        </Scrollable>
      </View>
    </Main>
  )
}
